import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type Dispatch,
  type Ref,
  type SetStateAction,
} from 'react';
import {
  categoriesOf,
  ITEMS_RECEIVED_LABEL,
  ORIGINS,
  ORIGIN_LABEL,
  PRESS_INGOTS,
  SHADES,
  SPEC_SCALE,
  SPEC_SCALE_LABEL,
  ZIRCONIA_BLOCKS,
  type ItemReceivedKey,
  type Origin,
  INSTRUCTIONS_MAX,
  SERIAL_NUMBER_MAX,
} from '@/domain';
import {
  countIsBelowChart,
  previewDeliveryDate,
  unidentifiedUnits,
  unitsFor,
} from '@/application/usecases/useIntake';
import { strings } from '@/shared/strings';
import { Odontogram } from '@/ui/data/Odontogram';
import { LockIcon } from '@/ui/icons';
import { ScaleChoice } from './SpecificationFields';
import { BridgesFields } from './BridgesFields';
import { ClientFields } from './ClientFields';
import { drawnSpans, type CaseFormValue } from './caseForm';
import styles from './IntakeScreen.module.css';

/**
 * The catalogue, grouped the way the price list groups it.
 *
 * Lifted out of the case field because every marked tooth offers the same
 * list. Rendering the options once per tooth is what makes the two impossible
 * to drift apart.
 */
function CatalogOptions({
  items,
  keep = [],
}: {
  items: { name: string; category: string; active?: boolean }[];
  /** Names already chosen: offered even once retired, so the select shows them. */
  keep?: readonly string[];
}) {
  return (
    <>
      {categoriesOf(items.map((item) => item.category)).map((category) => (
        <optgroup key={category} label={category}>
          {items
            .filter((item) => item.category === category)
            // A retired item is not offered for new work.
            .filter((item) => item.active !== false || keep.includes(item.name))
            .map((item) => (
              <option key={item.name}>{item.name}</option>
            ))}
        </optgroup>
      ))}
    </>
  );
}

/** Nothing wrong with either name: one object, so the default is the same on every render. */
const NO_IDENTITY_ERRORS = { doctor: null, patient: null } as const;

export interface CaseFieldsProps {
  value: CaseFormValue;
  onChange: Dispatch<SetStateAction<CaseFormValue>>;
  catalogItems: { name: string; category: string; active?: boolean }[];
  /**
   * Intake sets the two dates here. An edit leaves them to the order's own
   * control, which has its own permission and its own endpoint, so it passes null.
   */
  dates: { canEditDates: boolean; isCadcam: boolean } | null;
  /** The doctor and patient, for a caller who may read them. */
  identity: boolean;
  /**
   * What the server said against the doctor and the patient, each shown on its
   * field. Left off by a caller with nothing to say.
   */
  identityErrors?: { doctor: string | null; patient: string | null };
  /** The message against each span row, or null where the row is sound. */
  spanErrors: readonly (string | null)[];
  chartRef?: Ref<HTMLDivElement>;
}

/**
 * The fields of a case, from the doctor to the instructions. Sections 5.2 and
 * 11.1 of the specification, and system test handover 2.4: intake and the
 * edit screen both render this, so the two cannot drift.
 *
 * The rules that are specified rather than chosen: teeth are picked on the
 * chart, units follow the teeth and stay typable, and delivery previews as
 * start plus seven working days unless the caller holds `editDates`.
 */
export function CaseFields({
  value,
  onChange,
  catalogItems,
  dates,
  identity,
  identityErrors = NO_IDENTITY_ERRORS,
  spanErrors,
  chartRef,
}: CaseFieldsProps) {
  const ids = {
    origin: useId(),
    input: useId(),
    product: useId(),
    shade: useId(),
    shadeList: useId(),
    units: useId(),
    unitsNote: useId(),
    ingot: useId(),
    ingotList: useId(),
    block: useId(),
    blockList: useId(),
    stain: useId(),
    start: useId(),
    delivery: useId(),
    instructions: useId(),
    serial: useId(),
    serialHelp: useId(),
  };

  const showDates = dates !== null;
  const canEditDates = dates?.canEditDates ?? false;
  // What the box says right now, which is what the notes under it are about.
  const inBox = Number(value.unitsText) || 0;
  const units = unitsFor(value.teeth, inBox);
  const unnamed = unidentifiedUnits(value.teeth, inBox);
  const countTooLow = countIsBelowChart(value.teeth, inBox);

  /*
   * The number somebody typed into the box, kept apart from what the box shows.
   *
   * The box shows the EFFECTIVE count, the larger of the chart and this number,
   * and is rewritten whenever the chart changes and when the box loses focus.
   * This is the floor underneath it: somebody who typed six and then corrects
   * which teeth they meant still has a six unit case, and a case marked up to
   * seven teeth and back down to four reads six again rather than four. Reception
   * takes a case over the phone knowing there are six crowns before knowing which
   * six, and a unit they will bill must not be dropped quietly. Section 3.3 of
   * the request of 13 September 2026.
   *
   * A ref rather than state, so a chart change reads the number as it was last
   * typed rather than out of the box it is about to overwrite. It starts from
   * what the form opened with, which for an edit is the case's own count.
   */
  const typedUnits = useRef(Number(value.unitsText) || 0);

  function set<K extends keyof CaseFormValue>(key: K, next: CaseFormValue[K]) {
    onChange((current) => ({ ...current, [key]: next }));
  }

  // The preview follows the start date until somebody with editDates takes over.
  useEffect(() => {
    if (!showDates || canEditDates) return;
    onChange((current) => {
      const preview = previewDeliveryDate(current.startDate);
      return preview === current.deliveryDate ? current : { ...current, deliveryDate: preview };
    });
  }, [value.startDate, showDates, canEditDates, onChange]);

  /**
   * Writes the effective count into the box, the larger of the teeth marked and
   * the number typed. Empty when both are nothing, so the placeholder shows.
   */
  const reconcileUnits = useCallback(() => {
    onChange((current) => {
      const effective = unitsFor(current.teeth, typedUnits.current);
      const text = effective === 0 ? '' : String(effective);
      return text === current.unitsText ? current : { ...current, unitsText: text };
    });
  }, [onChange]);

  /*
   * On every change to the chart, up and down, and never on a keystroke: a box
   * that rewrites itself under the cursor cannot be edited, only appended to.
   * The blur on the input itself is the other moment it is brought in line.
   */
  useEffect(() => {
    reconcileUnits();
  }, [value.teeth.length, reconcileUnits]);

  /*
   * A newly marked tooth takes the case's item; an unmarked one is forgotten.
   *
   * Forgetting matters. Keeping the entry would mean that unmarking a tooth and
   * marking it again silently restored an item somebody had already corrected
   * away from, and the form would be sending a value nothing on screen showed.
   *
   * Changing the case item deliberately does NOT rewrite teeth already marked:
   * `current[tooth] ?? product` keeps what is there.
   */
  useEffect(() => {
    onChange((current) => {
      const next: Record<string, string> = {};
      for (const tooth of current.teeth) next[tooth] = current.toothProducts[tooth] ?? current.product;

      const unchanged =
        Object.keys(next).length === Object.keys(current.toothProducts).length &&
        current.teeth.every((tooth) => current.toothProducts[tooth] === next[tooth]);

      return unchanged ? current : { ...current, toothProducts: next };
    });
  }, [value.teeth, value.product, onChange]);

  return (
    <>
      {/*
        The doctor and the patient, each looked up as it is typed and tied to
        its record. The request of 6 October 2026 (second), items 4 and 5.
        Absent, both of them, for a caller who is not given the names.
      */}
      {identity && <ClientFields value={value} onChange={onChange} errors={identityErrors} />}

      {/*
        The number on the paper job order handed to the doctor, optional. The
        lab's request of 17 October 2026, item 7, in its own words.
      */}
      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.serial}>
            {strings.serialNumber.label} <span className={styles.note}>({strings.serialNumber.optional})</span>
          </label>
          <input
            id={ids.serial}
            name="serialNumber"
            className={styles.input}
            value={value.serialNumber}
            maxLength={SERIAL_NUMBER_MAX}
            autoComplete="off"
            aria-describedby={ids.serialHelp}
            onChange={(event) => set('serialNumber', event.target.value)}
          />
          <span id={ids.serialHelp} className={styles.note}>
            {strings.serialNumber.helper}
          </span>
        </div>
      </div>

      <div className={`${styles.row} ${styles.three}`}>
        {/*
          Where the case comes from, in place of the country. Section 1 of the
          request of 24 September 2026: the picker offered every country in the
          world, and the lab tells its cases apart by these three.
        */}
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.origin}>
            {strings.intake.origin}
          </label>
          <select
            id={ids.origin}
            name="origin"
            className={styles.select}
            value={value.origin}
            onChange={(event) => set('origin', event.target.value as Origin)}
          >
            {ORIGINS.map((origin) => (
              <option key={origin} value={origin}>
                {ORIGIN_LABEL[origin]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.input}>
            {strings.intake.inputType}
          </label>
          <select
            id={ids.input}
            name="input"
            className={styles.select}
            value={value.input}
            onChange={(event) =>
              set('input', event.target.value === 'impression' ? 'impression' : 'digital')
            }
          >
            <option value="digital">{strings.intake.inputDigital}</option>
            <option value="impression">{strings.intake.inputImpression}</option>
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.product}>
            {strings.intake.item}
            {value.teeth.length > 0 && <span className={styles.lock}>{strings.intake.itemHint}</span>}
          </label>
          <select
            id={ids.product}
            name="product"
            className={styles.select}
            value={value.product}
            onChange={(event) => set('product', event.target.value)}
          >
            <CatalogOptions items={catalogItems} />
          </select>
        </div>
      </div>

      <div className={`${styles.row} ${styles.one}`} ref={chartRef}>
        <div className={styles.field}>
          <span className={styles.l}>{strings.intake.teeth}</span>
          <Odontogram
            selected={value.teeth}
            onChange={(teeth) => set('teeth', teeth)}
            spans={drawnSpans(value.spanRows)}
            showSelection
          />
        </div>
      </div>

      {/*
        One item per marked tooth.

        Under the chart rather than beside the case item, because it is a
        reading of the chart. It is absent until a tooth is marked: a case
        taken over the phone on a unit count alone has no teeth to price
        individually yet.
      */}
      {value.teeth.length > 0 && (
        <div className={`${styles.row} ${styles.one}`}>
          <div className={styles.field}>
            <span className={styles.l}>
              {strings.intake.itemPerTooth}
              <span className={styles.lock}>{strings.intake.itemPerToothHint}</span>
            </span>
            <div className={styles.toothItems}>
              {value.teeth.map((tooth) => (
                <label key={tooth} className={styles.toothItem}>
                  <span className={styles.toothNo}>{tooth}</span>
                  <select
                    className={styles.select}
                    aria-label={`${strings.intake.item} ${tooth}`}
                    value={value.toothProducts[tooth] ?? value.product}
                    onChange={(event) => {
                      const product = event.target.value;
                      onChange((current) => ({
                        ...current,
                        toothProducts: { ...current.toothProducts, [tooth]: product },
                      }));
                    }}
                  >
                    <CatalogOptions items={catalogItems} />
                  </select>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/*
        Bridges and titanium bars, directly under the item per tooth and
        present whether or not a tooth is marked: the form has to say the
        feature exists. The chart above redraws as the rows change.
      */}
      <div className={`${styles.row} ${styles.one}`}>
        <BridgesFields
          rows={value.spanRows}
          teeth={value.teeth}
          errors={spanErrors}
          onChange={(rows) => set('spanRows', rows)}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.shade}>
            {strings.intake.shade}
          </label>
          {/*
            A suggestion list, not a closed set. The seven shades are what the
            lab stocks; a dropdown could not record a VITA 3D-Master shade or
            the "A3.5 body, A2 incisal" a dentist writes across the slip.
          */}
          <input
            id={ids.shade}
            name="shade"
            className={styles.input}
            list={ids.shadeList}
            value={value.shade}
            placeholder={strings.intake.shadePlaceholder}
            onChange={(event) => set('shade', event.target.value)}
          />
          <datalist id={ids.shadeList}>
            {SHADES.map((shade) => (
              <option key={shade} value={shade} />
            ))}
          </datalist>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.units}>
            {strings.intake.units}
          </label>
          {/*
            Always typable, marked chart or not. The two fill each other in and
            the larger wins, so a six unit case with four teeth identified can
            be recorded. The text is kept as typed while the box has focus, so
            it can be emptied and retyped; it is brought in line with the chart
            on blur.
          */}
          <input
            id={ids.units}
            className={styles.input}
            type="number"
            min={0}
            inputMode="numeric"
            value={value.unitsText}
            placeholder={strings.intake.unitsPlaceholder}
            onChange={(event) => {
              typedUnits.current = Number(event.target.value) || 0;
              set('unitsText', event.target.value);
            }}
            onBlur={reconcileUnits}
            aria-describedby={unnamed > 0 || countTooLow ? ids.unitsNote : undefined}
          />
          {countTooLow && (
            <span id={ids.unitsNote} className={styles.note}>
              {strings.intake.unitsBelowChart} {units} {strings.intake.unitsBelowChartTail}
            </span>
          )}
          {!countTooLow && unnamed > 0 && (
            <span id={ids.unitsNote} className={styles.note}>
              {value.teeth.length} {strings.intake.unitsMarked} {unnamed}{' '}
              {unnamed === 1 ? strings.intake.unitsUnnamed : strings.intake.unitsUnnamedPlural}
            </span>
          )}
        </div>
      </div>

      {dates !== null && (
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.l} htmlFor={ids.start}>
              {strings.intake.startDate}
              <span className={styles.lock}>
                <LockIcon />{' '}
                {dates.isCadcam ? strings.intake.startLockCadcam : strings.intake.startLockOther}
              </span>
            </label>
            <input
              id={ids.start}
              type="date"
              className={styles.input}
              value={value.startDate}
              onChange={(event) => set('startDate', event.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.l} htmlFor={ids.delivery}>
              {strings.intake.deliveryDate}
              <span className={styles.lock}>
                <LockIcon />{' '}
                {canEditDates ? strings.intake.deliveryLockEditable : strings.intake.deliveryLockAuto}
              </span>
            </label>
            <input
              id={ids.delivery}
              type="date"
              className={styles.input}
              value={value.deliveryDate}
              disabled={!canEditDates}
              onChange={(event) => set('deliveryDate', event.target.value)}
            />
          </div>
        </div>
      )}

      {/*
        The case specification. One ascending scale, spelled out: the paper
        form used single letters running in different directions on different
        rows, which is how the client came to ask what one of them meant.
      */}
      <div className={styles.sectionHead}>{strings.intake.specification}</div>

      <div className={styles.row}>
        <ScaleChoice
          name="translucency"
          label={strings.intake.translucency}
          options={SPEC_SCALE}
          labels={SPEC_SCALE_LABEL}
          value={value.spec.translucency}
          onChange={(translucency) =>
            onChange((current) => ({ ...current, spec: { ...current.spec, translucency } }))
          }
        />
        <ScaleChoice
          name="anatomy"
          label={strings.intake.anatomy}
          options={SPEC_SCALE}
          labels={SPEC_SCALE_LABEL}
          value={value.spec.anatomy}
          onChange={(anatomy) =>
            onChange((current) => ({ ...current, spec: { ...current.spec, anatomy } }))
          }
        />
      </div>

      <div className={styles.row}>
        <ScaleChoice
          name="incisal"
          label={strings.intake.incisal}
          options={SPEC_SCALE}
          labels={SPEC_SCALE_LABEL}
          value={value.spec.incisal}
          onChange={(incisal) =>
            onChange((current) => ({ ...current, spec: { ...current.spec, incisal } }))
          }
        />
        <ScaleChoice
          name="occlusal"
          label={strings.intake.occlusal}
          options={SPEC_SCALE}
          labels={SPEC_SCALE_LABEL}
          value={value.spec.occlusal}
          onChange={(occlusal) =>
            onChange((current) => ({ ...current, spec: { ...current.spec, occlusal } }))
          }
        />
      </div>

      {/*
        Three free text fields. A datalist suggests what the lab usually
        stocks without closing the set: a lab that has just opened a box of
        something new should not wait for a deploy to write its name down.
      */}
      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.ingot}>
            {strings.intake.ingot}
          </label>
          <input
            id={ids.ingot}
            className={styles.input}
            list={ids.ingotList}
            value={value.spec.ingot}
            placeholder={strings.intake.ingotPlaceholder}
            onChange={(event) => {
              const ingot = event.target.value;
              onChange((current) => ({ ...current, spec: { ...current.spec, ingot } }));
            }}
          />
          <datalist id={ids.ingotList}>
            {PRESS_INGOTS.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.block}>
            {strings.intake.block}
          </label>
          <input
            id={ids.block}
            className={styles.input}
            list={ids.blockList}
            value={value.spec.block}
            placeholder={strings.intake.blockPlaceholder}
            onChange={(event) => {
              const block = event.target.value;
              onChange((current) => ({ ...current, spec: { ...current.spec, block } }));
            }}
          />
          <datalist id={ids.blockList}>
            {ZIRCONIA_BLOCKS.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.stain}>
            {strings.intake.stain}
          </label>
          <input
            id={ids.stain}
            className={styles.input}
            value={value.spec.stain}
            placeholder={strings.intake.stainPlaceholder}
            onChange={(event) => {
              const stain = event.target.value;
              onChange((current) => ({ ...current, spec: { ...current.spec, stain } }));
            }}
          />
        </div>
      </div>

      {/* What physically arrived with the case. */}
      <div className={styles.sectionHead}>{strings.intake.itemsIncluded}</div>

      <div className={`${styles.row} ${styles.one}`}>
        <div className={styles.field}>
          <div className={styles.checks}>
            {(Object.keys(ITEMS_RECEIVED_LABEL) as ItemReceivedKey[]).map((key) => (
              <label key={key} className={styles.check}>
                <input
                  type="checkbox"
                  checked={value.items[key]}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onChange((current) => ({ ...current, items: { ...current.items, [key]: checked } }));
                  }}
                />
                {ITEMS_RECEIVED_LABEL[key]}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/*
        One instructions field. Section 5 of the request of 5 October 2026: there
        were two, the doctor's and the lab's own, and the second was a duplicate
        people filled in twice or not at all. What had been written in both was
        merged into this one, each part under its heading.
      */}
      <div className={`${styles.row} ${styles.one}`}>
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.instructions}>
            {strings.instructions.title}
          </label>
          <textarea
            id={ids.instructions}
            name="instructions"
            className={styles.textarea}
            placeholder={strings.intake.instructionsPlaceholder}
            maxLength={INSTRUCTIONS_MAX}
            value={value.instructions}
            onChange={(event) => set('instructions', event.target.value)}
          />
        </div>
      </div>
    </>
  );
}
